import { call, ForkEffect, put, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import {
  getInvites,
  getPermissions,
  inviteSubordinate,
  recallInvite,
  validateInvite,
  recalledInvitation,
} from '../../api/inviteApi';
import { toastStyles } from '../../utils/styles';
import { IGetInvitationsParams, IGetInvitationsResponse, Invitation, inviteActions } from './inviteSlice';
import { SortOrder } from '../../utils/enums/sort-order.enum';
import { userActions } from '../user/userSlice';

function* watchGetPermissions() {
  try {
    yield put(inviteActions.setInviteStatus(null));
    const response: AxiosResponse = yield call(getPermissions);
    yield put(inviteActions.setPermissions(response.data));
  } catch (error: any) {
    yield put(inviteActions.setInviteError('Failed to fetch permissions.'));
  }
}

function* watchInviteSubordinate(action: PayloadAction<{ emails: string[]; permissions: number[] }>) {
  try {
    yield put(inviteActions.setInviteStatus('Sending invite...'));
    yield put(inviteActions.setInviteError(null));
    yield put(inviteActions.setInvitationLoading(true));

    yield call(inviteSubordinate, action.payload);

    yield put(inviteActions.setInviteStatus('Invite sent successfully.'));
    toast.success('Invite sent successfully.', toastStyles.success);
    yield put(userActions.getUserAsync());
    yield put(
      inviteActions.getInvitationsAsync({ page: 1, limit: 10, sortBy: 'createdAt', sortOrder: SortOrder.DESC }),
    );
  } catch (error: any) {
    yield put(inviteActions.setInviteError('Failed to send invite.'));
    toast.error('Failed to send invite.', toastStyles.error);
  } finally {
    yield put(inviteActions.setInvitationLoading(false));
  }
}

function* watchFetchInvites({ payload }: { payload: IGetInvitationsParams }) {
  try {
    yield put(inviteActions.resetGetInvitations());
    yield put(inviteActions.setComponentStatus({ invitesLoading: true }));
    const response: AxiosResponse = yield call(getInvites, payload);
    yield put(inviteActions.setInvitations(response.data));
    yield put(inviteActions.setComponentStatus({ invitesLoading: false }));
  } catch (error: any) {
    yield put(
      inviteActions.setComponentStatus({
        error: error.response.data.message,
        getGemsLoading: false,
      }),
    );
  }
}

function* watchValidateInvite(action: ReturnType<typeof inviteActions.getValidateInviteAsync>) {
  const { payload } = action;
  try {
    yield put(inviteActions.setComponentStatus({ validateTokenLoading: true }));

    const response: AxiosResponse = yield call(validateInvite, payload);

    yield put(
      inviteActions.setInviteValidity({
        data: response.data,
        status: response.status,
      }),
    );

    yield put(inviteActions.setComponentStatus({ validateTokenLoading: false }));
  } catch (error: any) {
    yield put(
      inviteActions.setComponentStatus({
        error: error.response?.data?.message || 'Failed to validate invite',
        errorStatus: error.response?.status || 500,
        validateTokenLoading: false,
      }),
    );
  }
}

function* watchRecallInvite(action: ReturnType<typeof inviteActions.getRecallInviteAsync>) {
  const { payload } = action;
  try {
    yield put(inviteActions.setComponentStatus({ recallInviteLoading: true }));

    const response: AxiosResponse = yield call(recallInvite, payload);

    yield put(inviteActions.setComponentStatus({ recallInviteLoading: false, recallInviteStatus: response.status }));
  } catch (error: any) {
    yield put(
      inviteActions.setComponentStatus({
        error: error.response?.data?.message || 'Failed to recall invite',
        errorStatus: error.response?.status || 500,
        recallInviteLoading: false,
      }),
    );
  }
}

function* watchRecallInvitation(action: PayloadAction<string>) {
  const invitationId = action.payload;
  try {
    yield put(inviteActions.setComponentStatus({ recallInvitationLoading: true, invitesLoading: true }));

    const response: AxiosResponse = yield call(recalledInvitation, invitationId);

    yield put(inviteActions.recallInvitationSuccess({ id: invitationId }));

    yield put(
      inviteActions.setComponentStatus({
        recallInvitationLoading: false,
        recallInvitationStatus: response.status,
        invitesLoading: true,
      }),
    );

    toast.success(
      response.data.status !== 'Pending' ? 'Invite Recalled!' : 'Invite Resent!',
      response.data.status !== 'Pending' ? toastStyles.error : toastStyles.success,
    );
    yield put(userActions.getUserAsync());
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to recall invite';

    yield put(inviteActions.recallInvitationFailure(errorMessage));

    yield put(
      inviteActions.setComponentStatus({
        error: errorMessage,
        errorStatus: error.response?.status || 500,
        recallInvitationLoading: false,
        invitesLoading: false,
      }),
    );

    toast.error(errorMessage, toastStyles.error);
  }
}

export function* watchInviteSagas(): Generator<ForkEffect, void> {
  yield takeLatest(inviteActions.getPermissionsAsync.type, watchGetPermissions);
  yield takeLatest(inviteActions.inviteSubordinateAsync.type, watchInviteSubordinate);
  yield takeLatest(inviteActions.getValidateInviteAsync.type, watchValidateInvite);
  yield takeLatest(inviteActions.getRecallInviteAsync.type, watchRecallInvite);
  yield takeLatest(inviteActions.getInvitationsAsync, watchFetchInvites);
  yield takeLatest(inviteActions.recallInvitationAsync.type, watchRecallInvitation);
}

const inviteSagas = watchInviteSagas;
export default inviteSagas;
